import './fonts.css';
import './index.css';
import 'bootstrap/dist/css/bootstrap.css';
import 'bootstrap/dist/js/bootstrap.bundle';
import 'react-phone-number-input/style.css'
import '@material-symbols/font-400/outlined.css';
import 'react-datepicker/dist/react-datepicker.css';
import '@capacitor-community/safe-area';

import React, { Suspense } from 'react';
import ReactDOM from 'react-dom/client';
import reportWebVitals from './reportWebVitals';
import { Navigate, RouterProvider, createBrowserRouter } from 'react-router-dom';
import './i18n'; // i18n-Initialisierung

// Routes
import Root from './routes/root';
import ProtectedRoute from './routes/protected-route';
import PageContainerWrapper from './routes/page-container-wrapper';
import * as Sentry from "@sentry/react";
import useCookieConsentStore, { consentApplies, hasConsent } from "./stores/cookieConsentStore";
import FAQPage from './routes/knowledge-hub/sub-pages/faq-page/faq-page';
import { PWAProvider, usePWA } from './context/PWAContext';
import useInvestorDetailStore from './stores/investorDetailStore';
import i18nIsoCountries from "i18n-nationality";
import { Capacitor } from '@capacitor/core';
import useTenantDetailStore from './stores/tenantDetailStore';
import AppRedirect from './routes/app-redirect';
import ErrorBoundary from './components/error-boundary/error-boundary';
import TenantRegistrationPage from './routes/tenant-registration-page/tenant-registration-page';
import { initializeApp } from './appInit';
import { registerResumeUpdateCheck } from './capacitorListeners';
import { SafeArea } from '@capacitor-community/safe-area';
import { StyleSheetManager } from 'styled-components';

// Landing pages are intentionally NOT React.lazy (see comment near
// the lazy declarations below). They're imported eagerly at the top
// here to satisfy ESLint's `import/first` rule.
import HomeLandingPage from './routes/landing-page/sub-pages/home-page/home-page';
import TenantHomeLandingPage from './routes/landing-page/sub-pages/tenant-home-page/tenant-home-page';
import DetailsLandingPage from './routes/landing-page/sub-pages/details-page/details-page';
import LandingPageArticleDetailPage from './routes/landing-page/sub-pages/article-detail-page/article-detail-page';

SafeArea.enable({
  config: {
    customColorsForSystemBars: true,
    statusBarColor: '#00000000', // transparent
    statusBarContent: 'light',
    navigationBarColor: '#00000000', // transparent
    navigationBarContent: 'light',
  },
});

// Register i18n country locales
i18nIsoCountries.registerLocale(require("i18n-nationality/langs/de.json"));
i18nIsoCountries.registerLocale(require("i18n-nationality/langs/en.json"));

// Pages
const ErrorPage = React.lazy(() => import('./routes/error-page/error-page'));
const LoginPage = React.lazy(() => import('./routes/login-page/login-page'));
const ResetPasswordPage = React.lazy(() => import('./routes/reset-password-page/reset-password-page'));
const CoursePage = React.lazy(() => import('./routes/course-page'));
const MyUnitsPage = React.lazy(() => import('./routes/my-units-page/my-units-page'));
const DashboardPage = React.lazy(() => import('./routes/dashboard-page/dashboard-page'));
const ProfilePage = React.lazy(() => import('./routes/profile-page/profile-page'));
const CourseDetailPage = React.lazy(() => import('./routes/course-detail-page/course-detail-page'));
const UnitDetailPage = React.lazy(() => import('./routes/unit-detail-page/unit-detail-page'));
const PackageSelectionPage = React.lazy(() => import('./routes/package-selection-page/package-selection-page'));
const FinancialsPage = React.lazy(() => import('./routes/financials-page/financials-page'));
const ProjectPage = React.lazy(() => import('./routes/project-page/project-page'));
const ContractsOverviewPage = React.lazy(() => import('./routes/contracts-overview-page/contracts-overview-page'));
const ContractsPage = React.lazy(() => import('./routes/contracts-page/contracts-page'));
const ContractDocumentsPage = React.lazy(() => import('./routes/contract-documents-page/contract-documents-page'));
const KnowledgeHubPage = React.lazy(() => import('./routes/knowledge-hub/knowledge-hub'));
const ProfileBankDetailsPage = React.lazy(() => import('./routes/profile-page/sub-pages/bank-details-page/bank-details-page'));
const ProfileAddresDetailsPage = React.lazy(() => import('./routes/profile-page/sub-pages/address-details-page/address-details-page'));
const ProfilePersonalDetailsPage = React.lazy(() => import('./routes/profile-page/sub-pages/personal-details-page/personal-details-page'));
const ProfileSettingsPage = React.lazy(() => import('./routes/profile-page/sub-pages/settings-page/settings-page'));
const ConnectedAppsPage = React.lazy(() => import('./routes/profile-page/sub-pages/connected-apps-page/connected-apps-page'));
const UtilityAccountsPage = React.lazy(() => import('./routes/profile-page/sub-pages/utility-accounts-page/utility-accounts-page'));
const RequestsPage = React.lazy(() => import('./routes/requests-page/requests-page'));
const RequestDetailPage = React.lazy(() => import('./routes/request-detail-page/request-detail-page'));
const OAuthConsentPage = React.lazy(() => import('./routes/oauth-consent-page/oauth-consent-page'));
const ProfileEditAddresDetailsPage = React.lazy(() => import('./routes/profile-page/sub-pages/edit-address-details-page/edit-address-details-page'));
const ProfileEditBankDetailsPage = React.lazy(() => import('./routes/profile-page/sub-pages/edit-bank-details-page/edit-bank-details-page'));
const ProfileDocumentDetailsPage = React.lazy(() => import('./routes/profile-page/sub-pages/document-details-page/document-details-page'));
const PrivacyPolicyPage = React.lazy(() => import('./routes/privacy-policy-page/privacy-policy-page'));
const TermsAndConditionsPage = React.lazy(() => import('./routes/terms-and-conditions-page/terms-and-conditions-page'));
const KnowledgeHubArticleDetailPage = React.lazy(() => import('./routes/knowledge-hub/sub-pages/article-detail-page/article-detail-page'));
const OnboardingPage = React.lazy(() => import('./routes/onboarding-page/onboarding-page'));
// Landing pages are intentionally NOT React.lazy. We prerender these
// pages at build time (see scripts/prerender.js). When a lazy chunk is
// served via prerendered HTML, React hits a hydration mismatch on the
// first paint — it tries to render the Suspense fallback before the
// chunk loads, which doesn't match the prerendered DOM, so it replaces
// the entire tree → ~2s flash of unstyled content. Loading these
// eagerly keeps the main bundle slightly bigger but avoids the flash.
// (Actual imports are at the top of the file to satisfy ESLint.)
const HandoverPage = React.lazy(() => import('./routes/handover-page/handover-page'));
const BookValuationPage = React.lazy(() => import('./routes/book-valuation-page/book-valuation-page'));
const PaymentsPage = React.lazy(() => import('./routes/payments-page/payments-page'));
const MyUnitPage = React.lazy(() => import('./routes/my-unit-page/my-unit-page'));

const isProduction = process.env.REACT_APP_ENV === "production";

const replayIntegration = () =>
  Sentry.replayIntegration({
    maskAllText: true,
    maskAllInputs: true,
    blockAllMedia: false,
  });

// In the browser, session recordings and performance traces wait for the
// visitor's statistics consent; error reports do not.
Sentry.init({
  dsn: "https://352009d95c7053d2edcab1a5b0b50a3b@o4507089669849088.ingest.de.sentry.io/4507089726603344",
  integrations: [
    Sentry.browserTracingIntegration(),
    ...(consentApplies() ? [] : [replayIntegration()]),
  ],
  tracesSampler: () => (hasConsent("statistics") ? (isProduction ? 0.1 : 1.0) : 0),
  tracePropagationTargets: [
    "localhost",
    /^https:\/\/api\.getmanagd\.com/,
    /^https:\/\/api\.managd\.ae/
  ],
  // Session Replay
  replaysSessionSampleRate: isProduction ? 0.2 : 1.0,
  replaysOnErrorSampleRate: 1.0,
});

if (consentApplies()) {
  let replayAdded = false;
  const syncReplay = (allowed: boolean) => {
    if (allowed && !replayAdded) {
      Sentry.addIntegration(replayIntegration());
      replayAdded = true;
    } else if (!allowed && replayAdded) {
      // Withdrawn: stop now. Granting again resumes recording from the next page load.
      Sentry.getReplay()?.stop();
    }
  };
  syncReplay(hasConsent("statistics"));
  useCookieConsentStore.subscribe((state, previous) => {
    if (state.choice?.statistics !== previous.choice?.statistics) syncReplay(state.choice?.statistics === true);
  });
}

const RedirectToHome: React.FC = () => {
  const isPWA = usePWA();

  // Stores
  const investor = useInvestorDetailStore(state => state.investor);
  const tenant = useTenantDetailStore(state => state.tenant);

  const isCapacitor = Capacitor.isNativePlatform();
  const isMobileApp = isCapacitor || isPWA;

  if (investor || tenant) {
    return <Navigate to="/dashboard" replace />;
  }

  if (isMobileApp) {
    return <Navigate to="/login" replace />;
  }

  return <Navigate to="/home" replace />;
};

const router = createBrowserRouter([
  {
    path: "/",
    element: <Root />,
    errorElement: <ErrorPage />,
    children: [
      {
        path: "/",
        element: <RedirectToHome />
      },
      {
        path: "/download",
        element: <AppRedirect />
      },
      {
        path: "/dashboard",
        element: <Suspense fallback={
          <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '100vh' }}>
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading Dashboard...</span>
            </div>
          </div>
        }>
          <ProtectedRoute>
            <PageContainerWrapper id="dashboard">
              <DashboardPage />
            </PageContainerWrapper>
          </ProtectedRoute>
        </Suspense>,
      },
      {
        path: "/home",
        element: <HomeLandingPage />,
      },
      {
        path: "/tenant-home",
        element: <TenantHomeLandingPage />,
      },
      {
        path: "/details",
        element: <DetailsLandingPage />,
      },
      {
        path: "/articles/:id",
        element: <LandingPageArticleDetailPage />,
      },
      {
        path: "/onboarding",
        element: <ProtectedRoute allowedUserTypes="both">
          <PageContainerWrapper id="onboarding">
            <OnboardingPage />
          </PageContainerWrapper>
        </ProtectedRoute>,
      },
      {
        path: "/handover",
        element: <ProtectedRoute allowedUserTypes="investor">
          <PageContainerWrapper id="handover">
            <HandoverPage />
          </PageContainerWrapper>
        </ProtectedRoute>,
      },
      {
        path: "/book-valuation",
        element: <ProtectedRoute allowedUserTypes="investor">
          <PageContainerWrapper id="book-valuation">
            <BookValuationPage />
          </PageContainerWrapper>
        </ProtectedRoute>,
      },
      {
        path: "/profile",
        element: <ProtectedRoute>
          <PageContainerWrapper id="my-profile">
            <ProfilePage />
          </PageContainerWrapper>
        </ProtectedRoute>,
      },
      {
        path: "/profile/bank-details",
        element: <ProtectedRoute allowedUserTypes="investor">
          <PageContainerWrapper id="my-profile-bank-details">
            <ProfileBankDetailsPage />
          </PageContainerWrapper>
        </ProtectedRoute>,
      },
      {
        path: "/profile/edit-bank-details",
        element: <ProtectedRoute allowedUserTypes="investor">
          <PageContainerWrapper id="my-profile-edit-bank-details">
            <ProfileEditBankDetailsPage />
          </PageContainerWrapper>
        </ProtectedRoute>,
      },
      {
        path: "/profile/address-details",
        element: <ProtectedRoute allowedUserTypes="investor">
          <PageContainerWrapper id="my-profile-address-details">
            <ProfileAddresDetailsPage />
          </PageContainerWrapper>
        </ProtectedRoute>,
      },
      {
        path: "/profile/personal-details",
        element: <ProtectedRoute allowedUserTypes="tenant">
          <PageContainerWrapper id="my-profile-personal-details">
            <ProfilePersonalDetailsPage />
          </PageContainerWrapper>
        </ProtectedRoute>,
      },
      {
        path: "/profile/documents",
        element: <ProtectedRoute>
          <PageContainerWrapper id="my-profile-documents">
            <ProfileDocumentDetailsPage />
          </PageContainerWrapper>
        </ProtectedRoute>,
      },
      {
        path: "/profile/edit-address",
        element: <ProtectedRoute allowedUserTypes="investor">
          <PageContainerWrapper id="my-profile-edit-address">
            <ProfileEditAddresDetailsPage />
          </PageContainerWrapper>
        </ProtectedRoute>,
      },
      {
        path: "/profile/connected-apps",
        element: <ProtectedRoute allowedUserTypes="investor">
          <PageContainerWrapper id="connected-apps">
            <ConnectedAppsPage />
          </PageContainerWrapper>
        </ProtectedRoute>,
      },
      {
        path: "/profile/utility-accounts",
        element: <ProtectedRoute allowedUserTypes="investor">
          <PageContainerWrapper id="my-profile-utility-accounts">
            <UtilityAccountsPage />
          </PageContainerWrapper>
        </ProtectedRoute>,
      },
      {
        path: "/requests",
        element: <ProtectedRoute allowedUserTypes="both">
          <PageContainerWrapper id="requests">
            <RequestsPage />
          </PageContainerWrapper>
        </ProtectedRoute>,
      },
      {
        path: "/requests/:ticketId",
        element: <ProtectedRoute allowedUserTypes="both">
          <PageContainerWrapper id="request-detail">
            <RequestDetailPage />
          </PageContainerWrapper>
        </ProtectedRoute>,
      },
      {
        // Deliberately NOT behind ProtectedRoute: that would bounce an
        // unauthenticated person to the login without the way back, and the
        // request from the other platform would be lost. The page sends them
        // itself, carrying a redirect parameter.
        path: "/oauth/consent",
        element: <OAuthConsentPage />,
      },
      {
        // The same screen under the address the API publishes to integrators.
        // On the web nobody reaches this: api.getmanagd.com/oauth/authorize
        // redirects the browser to /oauth/consent above. On a device the app
        // claims the authorize address as a universal link and opens here
        // directly, because iOS decides from the address that was tapped and
        // does not follow the redirect to look again. The parameters are the
        // same either way, so the screen needs no branch.
        path: "/oauth/authorize",
        element: <OAuthConsentPage />,
      },
      {
        path: "/profile/settings",
        element: <ProtectedRoute>
          <PageContainerWrapper id="my-profile-settings">
            <ProfileSettingsPage />
          </PageContainerWrapper>
        </ProtectedRoute>,
      },
      {
        path: "/my-units",
        element: <ProtectedRoute allowedUserTypes="investor">
          <PageContainerWrapper id="my-units">
            <MyUnitsPage />
          </PageContainerWrapper>
        </ProtectedRoute>,
      },
      {
        path: "/my-units/:unitId",
        element: <ProtectedRoute allowedUserTypes="investor">
          <PageContainerWrapper id="my-units">
            <UnitDetailPage />
          </PageContainerWrapper>
        </ProtectedRoute>,
      },
      {
        path: "/course",
        element: <ProtectedRoute allowedUserTypes="investor">
          <PageContainerWrapper id="course">
            <CoursePage />
          </PageContainerWrapper>
        </ProtectedRoute>,
      },
      {
        path: "/course/:itemId",
        element: <ProtectedRoute allowedUserTypes="investor">
          <PageContainerWrapper id="course-detail">
            <CourseDetailPage />
          </PageContainerWrapper>
        </ProtectedRoute>,
      },
      {
        path: "/knowledge-hub",
        element: <ProtectedRoute allowedUserTypes="investor">
          <PageContainerWrapper id="knowledge-hub">
            <KnowledgeHubPage />
          </PageContainerWrapper>
        </ProtectedRoute>,
      },
      {
        path: "/knowledge-hub/articles/:id",
        element: <ProtectedRoute allowedUserTypes="investor">
          <PageContainerWrapper id="knowledge-hub-article">
            <KnowledgeHubArticleDetailPage />
          </PageContainerWrapper>
        </ProtectedRoute>,
      },
      {
        path: "/faq",
        element: <ProtectedRoute allowedUserTypes="investor">
          <PageContainerWrapper id="faq">
            <FAQPage />
          </PageContainerWrapper>
        </ProtectedRoute>,
      },
      {
        path: "/packages",
        element: <ProtectedRoute allowedUserTypes="investor">
          <PageContainerWrapper id="package-selection">
            <PackageSelectionPage />
          </PageContainerWrapper>
        </ProtectedRoute>,
      },
      {
        path: "/financials",
        element: <ProtectedRoute allowedUserTypes="investor">
          <PageContainerWrapper id="financials">
            <FinancialsPage />
          </PageContainerWrapper>
        </ProtectedRoute>,
      },
      {
        path: "/contracts",
        element: <ProtectedRoute allowedUserTypes="investor">
          <PageContainerWrapper id="contracts">
            <ContractsOverviewPage />
          </PageContainerWrapper>
        </ProtectedRoute>,
      },
      {
        path: "/contract-documents",
        element: <ProtectedRoute allowedUserTypes="investor">
          <PageContainerWrapper id="contracts-documents">
            <ContractDocumentsPage />
          </PageContainerWrapper>
        </ProtectedRoute>,
      },
      {
        path: "/contracts/:unitId",
        element: <ProtectedRoute allowedUserTypes="investor">
          <PageContainerWrapper id="unit-contracts">
            <ContractsPage />
          </PageContainerWrapper>
        </ProtectedRoute>,
      },
      {
        path: "/project/:propertyId",
        element: <ProtectedRoute allowedUserTypes="investor">
          <PageContainerWrapper id="project">
            <ProjectPage />
          </PageContainerWrapper>
        </ProtectedRoute>,
      },
      {
        path: "/payments",
        element: <ProtectedRoute allowedUserTypes="tenant">
          <PageContainerWrapper id="payments">
            <PaymentsPage />
          </PageContainerWrapper>
        </ProtectedRoute>,
      },
      {
        path: "/my-unit",
        element: <ProtectedRoute allowedUserTypes="tenant">
          <PageContainerWrapper id="my-unit">
            <MyUnitPage />
          </PageContainerWrapper>
        </ProtectedRoute>,
      },
      {
        path: "/login",
        element: <LoginPage />,
      },
      {
        path: "/reset-password",
        element: <PageContainerWrapper id="reset-password" fullPage>
          <ResetPasswordPage />
        </PageContainerWrapper>,
      },
      {
        path: "/privacy-policy",
        element: <PrivacyPolicyPage />,
      },
      {
        path: "/terms-and-conditions",
        element: <TermsAndConditionsPage />,
      },
      {
        path: "/tenant-registration",
        element: <TenantRegistrationPage />,
      }
    ],
  },
]);

const rootElement = document.getElementById('root') as HTMLElement;

// IMPORTANT: <StyleSheetManager disableCSSOMInjection> forces
// styled-components into the legacy text-based `<style>` tag mode
// instead of CSSOM constructable stylesheets. This is required for
// our build-time prerender to work — Puppeteer's `page.content()`
// captures HTML as text, which means CSSOM stylesheets are silently
// dropped (they live in JavaScript objects, not the DOM tree). With
// this flag styled-components writes its CSS into real `<style>` tags
// that survive serialisation, and the browser receives a fully styled
// prerender without the ~2s flash of unstyled content during hydration.
const appTree = (
  <React.StrictMode>
    <ErrorBoundary>
      <StyleSheetManager disableCSSOMInjection>
        <PWAProvider>
          <RouterProvider router={router} />
        </PWAProvider>
      </StyleSheetManager>
    </ErrorBoundary>
  </React.StrictMode>
);

// When the page was prerendered at build-time (see scripts/prerender.js),
// the static HTML already contains the rendered React tree. In that case
// we hydrate instead of re-rendering — this preserves the static HTML for
// crawlers AND lets React take over for real users without flickering /
// re-mounting the whole tree.
//
// Any other path (login, dashboard, profile, etc.) is served the same file
// with the prerendered home page in it. public/index.html marks those paths
// and hides that markup; here it is dropped and the page renders fresh, so
// a reload no longer shows the home page first.
const isPortalPath = document.documentElement.hasAttribute('data-spa-route');
if (isPortalPath) rootElement.replaceChildren();

if (rootElement.hasChildNodes()) {
  ReactDOM.hydrateRoot(rootElement, appTree);
} else {
  const root = ReactDOM.createRoot(rootElement);
  root.render(appTree);
}
document.documentElement.removeAttribute('data-spa-route');


reportWebVitals();

const setFavicon = () => {
  const env = process.env.REACT_APP_ENV; // Get environment
  const basePath = env === 'production' ? '/favicon/production/' : '/favicon/staging/';

  const icons = [
    { rel: 'icon', type: 'image/png', sizes: '32x32', href: `${basePath}favicon-32x32.png` },
    { rel: 'icon', type: 'image/png', sizes: '16x16', href: `${basePath}favicon-16x16.png` },
    { rel: 'apple-touch-icon', sizes: '180x180', href: `${basePath}apple-touch-icon.png` },
    { rel: 'icon', href: `${basePath}favicon.ico` }, // Default favicon
  ];

  icons.forEach(({ rel, type, sizes, href }) => {
    let link = document.querySelector(`link[rel='${rel}'][sizes='${sizes || ''}']`) as HTMLLinkElement | null;

    if (!link) {
      link = document.createElement('link') as HTMLLinkElement;
      link.rel = rel;
      if (type) link.setAttribute("type", type); // Use setAttribute
      if (sizes) link.setAttribute("sizes", sizes); // Use setAttribute
      document.head.appendChild(link);
    }
    link.href = href;
  });
};

// Call it at app startup
setFavicon();

const setManifest = () => {
  const env = process.env.REACT_APP_ENV;
  const basePath = env === 'production' ? '/favicon/production/' : '/favicon/staging/';

  const manifest = {
    name: "Managd - Investor Portal",
    short_name: "Managd",
    icons: [
      { src: `${basePath}android-chrome-192x192.png`, sizes: "192x192", type: "image/png" },
      { src: `${basePath}android-chrome-512x512.png`, sizes: "512x512", type: "image/png" }
    ],
    theme_color: "#ffffff",
    background_color: "#ffffff",
    display: "standalone"
  };

  const stringifiedManifest = JSON.stringify(manifest);
  const blob = new Blob([stringifiedManifest], { type: "application/json" });
  const manifestURL = URL.createObjectURL(blob);

  let link = document.querySelector("link[rel='manifest']") as HTMLLinkElement | null;
  if (!link) {
    link = document.createElement("link") as HTMLLinkElement;
    link.rel = "manifest";
    link.setAttribute("href", manifestURL);
    document.head.appendChild(link);
  } else {
    link.setAttribute("href", manifestURL);
  }
};

// Call it at app startup
setManifest();

window.addEventListener("error", (event) => {
  if (event.message && event.message.includes("Loading chunk")) {
    console.warn("Chunk error detected, reloading page...");
    window.location.reload();
  }
});

window.addEventListener("unhandledrejection", (event) => {
  if (
    event.reason &&
    event.reason.message &&
    event.reason.message.includes("Loading chunk")
  ) {
    console.warn("Chunk error detected in Promise, reloading page...");
    window.location.reload();
  }
});

// Add platform-specific classes to body for conditional styling
const setPlatformClasses = () => {
  const body = document.body;
  const html = document.documentElement;

  if (Capacitor.isNativePlatform()) {
    body.classList.add('capacitor-native');
    html.classList.add('capacitor-native');
  }
};

// Call it at app startup
setPlatformClasses();

registerResumeUpdateCheck();
void initializeApp();